import {useCallback, useEffect, useState} from 'react';
import type {RefObject} from 'react';
import type {CanvasIframeHandle} from '@continuum/canvas-runtime';
import {printCanvasSnapshot} from './print-snapshot';

/**
 * Owns the V2 canvas print flow for a viewer page: capture an inert snapshot
 * of the runtime iframe and hand it to the browser's print dialog.
 *
 * Cmd/Ctrl+P arrives via two different paths, so the hook handles both:
 *  - focus inside the iframe — the runtime forwards the intent and the host
 *    calls the returned `onPrintShortcut` (the keydown never reaches the
 *    parent when focus is in the null-origin frame);
 *  - focus in the parent — handled by the window listener installed here,
 *    which cancels the native print (it would slice the cross-origin frame
 *    instead of paginating it) and routes to the snapshot flow.
 *
 * `enabled` gates the page-level hijack so non-V2 canvases keep native print.
 */
export function useCanvasPrint({
  iframeRef,
  enabled,
}: {
  iframeRef: RefObject<CanvasIframeHandle | null>;
  enabled: boolean;
}): {onPrintShortcut: () => void} {
  const [isPrinting, setIsPrinting] = useState(false);

  const print = useCallback(async () => {
    if (isPrinting) {
      return;
    }
    setIsPrinting(true);
    try {
      const snapshot = await iframeRef.current?.captureSnapshot();
      if (snapshot?.html) {
        printCanvasSnapshot(snapshot);
      }
    } catch {
      // Snapshot timed out or the iframe unmounted — nothing actionable for
      // the viewer; leave the page as-is.
    } finally {
      setIsPrinting(false);
    }
  }, [isPrinting, iframeRef]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        !e.altKey &&
        !e.shiftKey &&
        (e.key === 'p' || e.key === 'P')
      ) {
        e.preventDefault();
        void print();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [enabled, print]);

  return {onPrintShortcut: print};
}
